import { CreditCard, Gift, HandCoins, PartyPopper, SlidersHorizontal, Undo2, Wallet } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../../lib/cn'
import { formatDate, formatSignedAmount } from '../../lib/format'
import type { WalletEntry, WalletEntryKind } from '../../lib/types'
import { Button, EmptyState } from '../ui'

// Display taxonomy per build-contract §3.7 — the server decides the kind,
// the client only maps it to a glyph + label.
const KIND_META: Record<WalletEntryKind, { label: string; icon: LucideIcon }> = {
  top_up: { label: 'Top-up', icon: CreditCard },
  pool_contribution: { label: 'Chipped in', icon: HandCoins },
  pool_withdrawal: { label: 'Took back', icon: Undo2 },
  gift_funding: { label: 'Gift funded', icon: Gift },
  // The recipient's side of a funded gift — a credit, so the shared
  // positive-amount styling below renders it like a top_up.
  gift_received: { label: 'Gift received', icon: PartyPopper },
  adjustment: { label: 'Adjustment', icon: SlidersHorizontal },
}

interface LedgerListProps {
  entries: WalletEntry[]
  hasMore?: boolean
  onLoadMore?: () => void
  loadingMore?: boolean
  className?: string
}

/**
 * The wallet ledger — signed, append-only history straight from the server.
 * Amounts render via formatSignedAmount (+$10.00 / −$4.99); context lines show
 * the group/game a move belonged to.
 */
export function LedgerList({ entries, hasMore = false, onLoadMore, loadingMore = false, className }: LedgerListProps) {
  if (entries.length === 0) {
    return (
      <EmptyState
        icon={<Wallet className="h-10 w-10" strokeWidth={1.5} />}
        title="Your wallet is empty"
        description="Add funds to chip in on group gifts."
        className={className}
      />
    )
  }

  return (
    <div className={className}>
      <ul className="divide-y divide-border">
        {entries.map((entry) => {
          const meta = KIND_META[entry.kind]
          const Icon = meta.icon
          const context = [entry.game_name, entry.group_name].filter(Boolean).join(' · ')
          const date = formatDate(entry.created_at)
          return (
            <li key={entry.id} className="flex items-center gap-3 py-3">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-text-muted ring-1 ring-border"
                aria-hidden="true"
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-text">{meta.label}</span>
                {(context || date) && (
                  <span className="block truncate text-xs text-text-muted">
                    {[context, date].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              <span
                className={cn(
                  'shrink-0 text-sm font-medium tabular-nums',
                  entry.amount_cents > 0 ? 'text-success-fg' : 'text-text',
                )}
              >
                {formatSignedAmount(entry.amount_cents, entry.currency)}
              </span>
            </li>
          )
        })}
      </ul>
      {hasMore && onLoadMore && (
        <div className="flex justify-center pt-3">
          <Button variant="secondary" size="sm" onClick={onLoadMore} loading={loadingMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  )
}
